import { Component, Input, OnInit } from '@angular/core';
import { BasicPieChartConfig } from '@app/data/interface/widget-config';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import moment from 'moment-timezone';
import { EChartsOption } from 'echarts';
import { BaseFilteredEnvironmentalDataChartComponent } from '../base-filtered-environmental-data-chart-component';
import { forceChartOptionsRerender } from '../helpers/widget-render.utils';

@Component({
  selector: 'app-macrophytes-pie-chart',
  templateUrl: './macrophytes-pie-chart.component.html',
  styleUrls: ['./macrophytes-pie-chart.component.scss'],
})
export class MacrophytesPieChartComponent extends BaseFilteredEnvironmentalDataChartComponent {
  @Input() config!: BasicPieChartConfig;
  chartOptions: EChartsOption | null = null;
  id: string = 'macrophytes-pie-chart-x';

  ngOnInit() {
    super.ngOnInit();
    this.id = `macrophytes-pie-chart-${this.config.dataConfig.widgetId}`;
    this.chartTitle = this.config.dataConfig.title || '';
    this.chartSubtitle = this.config.dataConfig.subtitle || '';
    this.domain = this.config.dataConfig.domain || '';
  }

  /**
   * Clear any displayed chart data so the UI does not show stale results while
   * new data is being fetched. Implements the abstract contract from the base class.
   */
  protected clearChartData(): void {
    this.chartOptions = null;
    this.hasData = false;
  }

  calculatePieChartData = (series: any) => {
    const aggregatedData = { native: 0, exotic: 0, unknown: 0 };

    if (series.data && series.entities) {
      series.data.forEach((row: any) => {
        const entityId = row.observation_entity_id;
        const entity = series.entities.find((e: any) => e.id === entityId);

        if (entity && row.sum != null) {
          if (entity.tags.includes('native')) {
            aggregatedData.native += row.sum;
          } else if (entity.tags.includes('exotic')) {
            aggregatedData.exotic += row.sum;
          } else {
            aggregatedData.unknown += row.sum;
          }
        }
      });
    }
    return aggregatedData;
  };

  setWidgetData() {
    this.hasData = false;

    // If we don't have any data sources to look at, exit early.
    const preserveConfiguredLabels = Boolean(this.config?.dataConfig?.preserveConfiguredDataSourceLabels);
    const applicableDataSources = this.selectDataSources(this.config.dataConfig.dataSources, {
      preserveConfiguredLabels,
    });

    // If there's nothing to display....
    if (applicableDataSources.length === 0) {
      return;
    }

    const newChartOptions: EChartsOption = this.config.echartsConfig;

    this.chartOptions = null;

    forkJoin(
      applicableDataSources.map((source: any) =>
        this.chartService
          .getEnvironmentalDataForSourceAndAnalyteByDate(
            source.environmentalDataSourceIdentifier,
            source.analyteIdentifier,
            moment(this.startDate).format('YYYY-MM-DD'),
            moment(this.endDate).format('YYYY-MM-DD'),
            this.startDate === this.endDate ? 'hour' : 'day'
          )
          .pipe(take(1))
      )
    ).subscribe({
      next: (queryResponses: any[]) => {
        const pieData: { name: string; value: number }[] = [];

        if (queryResponses.length > 0) {
          const aggregatedData = this.calculatePieChartData(queryResponses[0]);
          const total = aggregatedData.native + aggregatedData.exotic + aggregatedData.unknown;

          this.hasData = total > 0;

          if (aggregatedData.native > 0) {
            pieData.push({
              name: 'Native',
              value: aggregatedData.native,
            });
          }

          if (aggregatedData.exotic > 0) {
            pieData.push({
              name: 'Exotic',
              value: aggregatedData.exotic,
            });
          }

          if (aggregatedData.unknown > 0) {
            pieData.push({
              name: 'Unknown',
              value: aggregatedData.unknown,
            });
          }
        }

        if (!this.hasData) {
          this.chartOptions = {
            color: ['#6AB6B0'],
            title: {
              text: 'No data is available for the selected date range.',
            },
          };
          return;
        }

        newChartOptions.series = [
          {
            type: 'pie',
            center: ['50%', '50%'],
            radius: ['0%', '70%'],
            itemStyle: {
              borderRadius: 4,
            },
            label: {
              overflow: 'break',
              formatter: '{b} {d}%',
            },
            tooltip: {
              formatter: '{b}',
            },
            data: pieData,
          },
        ];

        this.chartOptions = newChartOptions;
      },
      error: (err: any) => {
        console.error(err);
        this.chartOptions = {
          color: ['#6AB6B0'],
          title: {
            text: 'No data is available.',
          },
        };
      },
    });
  }

  protected onExpandedStateChanged(expanded: boolean): void {
    super.onExpandedStateChanged(expanded);
    forceChartOptionsRerender(this);
  }
}
